// The one virtualized table. Owns the scroll container, the column template,
// the header, and the four body states that seven screens each hand-rolled.
//
// WHY THIS EXISTS
// ---------------
// The scroll container used to be written out by every screen, and one of them
// (dashboard) put it behind an early return while the page was loading. The
// virtualizer therefore never measured anything and `/campaigns` rendered a
// header above a blank body — see the header comment in `virtual-table.tsx`.
// Here the ref target and the measuring hook live in the SAME component, and
// that component renders its container on every code path, so an ancestor
// returning early above it is harmless: the table simply does not exist yet,
// and when it does it measures itself.
//
// DOM SHAPE IS LOAD-BEARING — DO NOT WRAP ANYTHING
// ------------------------------------------------
// Three CSS rules in styles.css are direct-child (`>`) scoped, and breaking
// them fails silently by reverting to a completely different table layout
// rather than erroring:
//   * `.tbl-frame > .tbl-wrap.vt` (:1926) — the wrap must be THIS component's
//     root element. Returning it inside another div loses the flex fill, and
//     `contain: strict` (:1814) then sizes it to nothing.
//   * `.tbl-wrap.vt > .tbl` (:1816 and ~20 more) — every virtualized rule is
//     scoped through this. An intermediate element reverts the table to the
//     plain `.tbl` styling: real table layout, collapsed borders, no grid.
//   * `.tbl-wrap.vt > .tbl tbody td > *` (:1898) sets `min-width: 0` on a
//     cell's IMMEDIATE children, which is what gives cells their ellipsis
//     behaviour. So `renderRow` returns the `<td>` elements themselves and this
//     component never wraps their contents.

import {
  Children,
  Fragment,
  isValidElement,
  useMemo,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useMediaQuery, usePhone, useTouch } from '@/lib/responsive';
import { MobileRecords } from './mobile-records';
import { QueryErrorNote } from '@/components/error-panel';
import { useVirtualRows, vtCols } from '@/components/virtual-table';

type CSSVarStyle = CSSProperties & { [k: `--${string}`]: string | number };

export type MobileRole = 'title' | 'subtitle' | 'status' | 'stat' | 'hidden';
export interface MobileSpec {
  role: MobileRole;
  /** Card label; the header's text otherwise. */
  label?: string;
  /** Position among the card's stats (1–4); declaration order otherwise. */
  order?: number;
  /** Render into the cell of the stat column `with`, `before` or `after` its value, joined by `separator`. */
  join?: { with: string; separator: string; order?: 'before' | 'after' };
}
export function mobileSpec(col: { mobile?: MobileRole | MobileSpec }): MobileSpec {
  const m = col.mobile ?? 'hidden';
  return typeof m === 'string' ? { role: m } : m;
}

export interface VtColumn {
  /** Stable id. Used as the React key and to look up sticky offsets. */
  key: string;
  header: ReactNode;
  /**
   * How much width this column earns. 1 (default) is always shown; 2 leaves
   * the grid below 1024px; 3 leaves it below 1280px. A sticky column must be
   * priority 1 — `createDataTable` asserts it, because the pinned offsets are
   * computed once for every width. Hiding is a change to the grid template,
   * never `display: none` on a cell: every row is a CSS grid, so a missing
   * item would slide every later cell one track left.
   */
  priority?: 1 | 2 | 3;
  /**
   * The column's place on a phone card: the `title`, a `subtitle` line under
   * it, a `status` chip on the right, a `stat` in the 2×2 grid (the first
   * four, in declaration order), or `hidden` (the default) — left to the
   * row's detail view. The object form adds a card-only `label` and `join`:
   * a stat that renders *into* another stat's cell (spend into budget as
   * "$1,796 / $15,010"; an end date after a start date as "Aug 27 → Oct 1").
   */
  mobile?: MobileRole | MobileSpec;
  /**
   * Minimum width in px — the column still grows as `minmax(Npx, Nfr)`. A
   * string passes through to the grid template unchanged, for a column that
   * must never grow (e.g. '36px').
   */
  width: number | string;
  /** `num` right-aligns with tabular figures; `center` centres. */
  align?: 'num' | 'center';
  /**
   * Pins the column horizontally. Only a LEADING run of columns may be sticky —
   * `stickyOffsets` asserts it, because a gap would silently mis-position every
   * pinned column after it.
   */
  sticky?: boolean;
  /** Header tooltip. */
  title?: string;
  /** Native table sort state for sortable columns. */
  ariaSort?: 'ascending' | 'descending' | 'none';
}

/**
 * Left offset for each sticky column, as the running sum of the widths before
 * it.
 *
 * Dashboard hand-wrote `left: 0 / 200 / 400` on six elements — three `<th>` and
 * three `<td>` — with nothing tying them to `vtCols(200, 200, 110, …)`. Editing
 * a column width silently mis-pinned the table. These are the same numbers,
 * derived.
 *
 * Numeric widths are minimums (`minmax(Npx, Nfr)`). A fixed `<number>px`
 * string is also measurable, which lets compact report tables keep their
 * sticky offsets tied exactly to their fixed grid tracks.
 */
export function stickyOffsets(columns: readonly VtColumn[]): (number | undefined)[] {
  const offsets: (number | undefined)[] = [];
  let run = 0;
  let ended = false;
  for (const col of columns) {
    if (!col.sticky) {
      ended = true;
      offsets.push(undefined);
      continue;
    }
    if (ended) {
      throw new Error(
        `data-table: sticky columns must be a leading run; "${col.key}" is sticky ` +
          'but follows a non-sticky column. `left` offsets would be wrong for it.',
      );
    }
    offsets.push(run);
    if (typeof col.width === 'number') {
      run += col.width;
    } else {
      const fixedPx = /^(\d+(?:\.\d+)?)px$/.exec(col.width.trim());
      if (fixedPx) run += Number(fixedPx[1]);
    }
  }
  return offsets;
}

/** The `<td>` elements a `renderRow` returned, flattened through fragments. */
export function cells(node: ReactNode): ReactElement<{ children?: ReactNode }>[] {
  return Children.toArray(node).flatMap((child) => {
    if (!isValidElement<{ children?: ReactNode }>(child)) return [];
    return child.type === Fragment ? cells(child.props.children) : [child];
  });
}

/** A header's plain text, reading through a sort control's `label` prop. */
export function textLabel(node: ReactNode): string {
  return Children.toArray(node)
    .map((child) =>
      typeof child === 'string' || typeof child === 'number'
        ? String(child)
        : isValidElement<{ children?: ReactNode; label?: ReactNode }>(child)
          ? textLabel(child.props.label ?? child.props.children)
          : '',
    )
    .join(' ')
    .trim();
}

/** The columns still shown when only priorities up to `max` fit. */
export function columnsUpTo(columns: readonly VtColumn[], max: 1 | 2 | 3): readonly VtColumn[] {
  return columns.filter((col) => (col.priority ?? 1) <= max);
}

// Tablet widths, where the grid drops its lower-priority columns. Below 768px
// the table is cards (or, for `keepTableOnMobile`, the narrowest column set).
const TABLET_QUERY = '(min-width: 768px) and (max-width: 1279px)';
const NARROW_TABLET_QUERY = '(min-width: 768px) and (max-width: 1023px)';

function headClass(col: VtColumn): string | undefined {
  const parts = [col.align, col.sticky ? 'sticky-c' : undefined].filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
}

export interface VtQueryLike {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

/** A query this table's rows depend on, and the noun to name it by. */
export interface VtDependency {
  what: string;
  query: VtQueryLike;
}

export type VtBodyState =
  | { kind: 'rows' }
  | { kind: 'loading'; what: string }
  | { kind: 'error'; what: string; error: unknown; retry: () => void }
  | { kind: 'empty' }
  | { kind: 'no-match' };

/**
 * Which of the four body states a table is in — the gate that was copy-pasted,
 * with three different orderings and two different arm counts, across seven
 * screens.
 *
 * `unfilteredTotal` is the row count BEFORE the screen's client-side filters,
 * which is the only way to tell "there is nothing here" from "nothing matches
 * what you typed".
 *
 * Pending and error only take the body when there is nothing to show. A failed
 * background refetch must never blank a table that is already populated — the
 * screens that got this right (productions, venues, events, pixels) all gate
 * their arms on a zero count, and this makes that the rule.
 */
export function resolveBodyState(a: {
  deps: readonly VtDependency[];
  total: number;
  unfilteredTotal: number;
}): VtBodyState {
  if (a.unfilteredTotal === 0) {
    const failed = a.deps.find((d) => d.query.isError);
    if (failed) {
      return {
        kind: 'error',
        what: failed.what,
        error: failed.query.error,
        retry: () => {
          void failed.query.refetch();
        },
      };
    }
    const pending = a.deps.find((d) => d.query.isPending);
    if (pending) return { kind: 'loading', what: pending.what };
    return { kind: 'empty' };
  }
  return a.total === 0 ? { kind: 'no-match' } : { kind: 'rows' };
}

export interface DataTableProps<T extends { id: string | number }> {
  rows: readonly T[];
  /** Row height in px. Screens legitimately differ: 36, 44, 52 and 56 are all in use. */
  rowH: number;
  /** Row count before the screen's own filters. Separates "empty" from "no match". */
  unfilteredTotal: number;
  /** Queries the rows come from. Empty means the table never reports loading or error. */
  deps?: readonly VtDependency[];
  /** Shown when the table has no rows at all. */
  empty: ReactNode;
  /** Shown when there are rows but the screen's filters exclude all of them. */
  noMatch?: ReactNode;
  /** Overrides the derived "Loading {what}…". */
  loading?: ReactNode;
  /** Returns this row's `<td>` elements — and only those. See the DOM note above. */
  renderRow: (row: T, index: number) => ReactNode;
  rowClassName?: (row: T) => string | undefined;
  /** Merged over the positioning transform. For per-row opacity or background. */
  rowStyle?: (row: T) => CSSProperties | undefined;
  onRowClick?: (row: T) => void;
  overscan?: number;
  /** Extra wrapper modifier (e.g. `white-tbl`). The `tbl-wrap vt` classes stay. */
  className?: string;
  /** Accessible name of the scrolling region. */
  label?: string;
  /**
   * Keep the grid on phones instead of cards — for a genuine analytical
   * matrix whose columns must be compared side by side. It renders the
   * priority-1 column set and scrolls sideways.
   */
  keepTableOnMobile?: boolean;
  /**
   * How many stats a phone card shows, two per row. Four (a 2×2 grid) unless
   * a screen's cards need more: Campaigns shows Tickets and ROAS beside its
   * four delivery stats.
   */
  mobileStats?: number;
}

/**
 * Declare a screen's columns once, at module scope, and get back its table plus
 * a `sticky` helper for the pinned cells in `renderRow`.
 *
 * This collapses four things that used to be maintained in parallel and drift:
 * the `vtCols(...)` widths, the hand-written `<th>` list, the `colSpan={N}` on
 * every empty-state row, and dashboard's hardcoded sticky `left` values.
 */
export function createDataTable(columns: readonly VtColumn[]) {
  const template = vtCols(...columns.map((c) => c.width));
  const offsets = stickyOffsets(columns);
  const colSpan = columns.length;
  for (const col of columns) {
    if (col.sticky && (col.priority ?? 1) !== 1) {
      throw new Error(
        `data-table: sticky column "${col.key}" must be priority 1; the pinned offsets are computed for every width.`,
      );
    }
  }
  // One column set per width band, precomputed like the desktop template so
  // a resize never recomputes them per row.
  const sets = {
    3: { columns, template, colSpan },
    2: { columns: columnsUpTo(columns, 2), template: vtCols(...columnsUpTo(columns, 2).map((c) => c.width)), colSpan: columnsUpTo(columns, 2).length },
    1: { columns: columnsUpTo(columns, 1), template: vtCols(...columnsUpTo(columns, 1).map((c) => c.width)), colSpan: columnsUpTo(columns, 1).length },
  } as const;
  const keptIndex = {
    3: new Set(columns.map((_, i) => i)),
    2: new Set(columns.flatMap((c, i) => ((c.priority ?? 1) <= 2 ? [i] : []))),
    1: new Set(columns.flatMap((c, i) => ((c.priority ?? 1) <= 1 ? [i] : []))),
  } as const;

  /**
   * Props for a pinned `<td>`. The body uses numbered classes (`sticky-c-1`)
   * while the header uses the bare `sticky-c`; both are load-bearing, including
   * for the hover repaint rules at styles.css:1882-1884.
   */
  function sticky(index: number, extra?: string) {
    const left = offsets[index];
    if (left === undefined) {
      throw new Error(`data-table: column ${index} is not sticky; sticky(${index}) is a mistake.`);
    }
    return {
      className: `sticky-c-${index + 1}${extra ? ` ${extra}` : ''}`,
      style: { left } as CSSProperties,
    };
  }

  function DesktopTable<T extends { id: string | number }>({
    rows,
    rowH,
    unfilteredTotal,
    deps = [],
    empty,
    noMatch = 'Nothing matches these filters.',
    loading,
    renderRow,
    rowClassName,
    rowStyle,
    onRowClick,
    overscan,
    className,
    label = 'Records',
    maxPriority = 3,
  }: DataTableProps<T> & { maxPriority?: 1 | 2 | 3 }) {
    const set = sets[maxPriority];
    const kept = keptIndex[maxPriority];
    const { visible, startIdx, totalH, containerRef } = useVirtualRows({ rows, rowH, overscan });
    const state = useMemo(
      () => resolveBodyState({ deps, total: rows.length, unfilteredTotal }),
      [deps, rows.length, unfilteredTotal],
    );

    const wrapStyle: CSSVarStyle = { '--vt-cols': set.template, '--vt-row-h': `${rowH}px` };
    // Reduced sets drop the cells of hidden columns. A renderRow whose cell
    // count differs from the column count cannot be trimmed safely (a
    // conditional <td> would shift the alignment), so it renders whole.
    const trim = (node: ReactNode): ReactNode => {
      if (maxPriority === 3) return node;
      const all = cells(node);
      if (all.length !== columns.length) return node;
      return all.filter((_, i) => kept.has(i));
    };
    const bodyStyle: CSSVarStyle = { '--vt-body-h': `${totalH}px` };

    return (
      // Rendered on EVERY path: no prop suppresses it, and no ref crosses out
      // of this component. That is what makes the blank-body bug unrepresentable.
      // Focusable so a grid that overflows sideways (a phone keeping its table,
      // a tablet with many pinned columns) scrolls by keyboard as well.
      <div
        className={`tbl-wrap vt${className ? ` ${className}` : ''}`}
        ref={containerRef}
        style={wrapStyle}
        role="region"
        aria-label={label}
        tabIndex={0}
      >
        <table className="tbl">
          <thead>
            <tr>
              {set.columns.map((col, i) => (
                <th
                  key={col.key}
                  className={headClass(col)}
                  title={col.title}
                  aria-sort={col.ariaSort}
                  style={offsets[i] !== undefined ? { left: offsets[i] } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody style={bodyStyle}>
            {state.kind !== 'rows' && (
              <tr className="vt-empty">
                <td colSpan={set.colSpan}>
                  {state.kind === 'loading' && (loading ?? `Loading ${state.what}…`)}
                  {state.kind === 'error' && (
                    <QueryErrorNote error={state.error} what={state.what} onRetry={state.retry} />
                  )}
                  {state.kind === 'empty' && empty}
                  {state.kind === 'no-match' && noMatch}
                </td>
              </tr>
            )}
            {visible.map((row, vi) => {
              const i = startIdx + vi;
              return (
                <tr
                  key={row.id}
                  className={rowClassName?.(row)}
                  style={{ transform: `translateY(${i * rowH}px)`, ...rowStyle?.(row) }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {trim(renderRow(row, i))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function Table<T extends { id: string | number }>(props: DataTableProps<T>) {
    const phone = usePhone();
    const touchLayout = useTouch();
    const tablet = useMediaQuery(TABLET_QUERY);
    const narrowTablet = useMediaQuery(NARROW_TABLET_QUERY);
    if (phone && !props.keepTableOnMobile) return <MobileRecords {...props} columns={columns} />;
    const maxPriority: 1 | 2 | 3 = phone || narrowTablet ? 1 : tablet ? 2 : 3;
    return (
      <DesktopTable
        {...props}
        maxPriority={maxPriority}
        rowH={touchLayout ? Math.max(48, props.rowH) : props.rowH}
      />
    );
  }
  return { Table, sticky, columns, colSpan, template };
}
